const DATABASE_NAME = "klint-route-logs";
const DATABASE_VERSION = 2;
const LOGS_STORE = "logs";
const SAMPLES_STORE = "samples";
const MOTOR_EVENTS_STORE = "motor-events";
const MAN_OVERBOARD_STORE = "man-overboard";

export interface LoggedWind {
  /** Interpolated wind speed, stored canonically in knots. */
  speedKnots: number;
  /** Meteorological direction the wind is coming from, in true degrees. */
  direction: number;
  observedAt: number;
  nearestStation: string;
  nearestDistanceKm: number;
  stationCount: number;
  source: "SMHI-interpolated";
}

export interface RouteLogSample {
  id?: number;
  logId: string;
  recordedAt: number;
  longitude: number;
  latitude: number;
  accuracyMetres: number | null;
  /** Raw speed reported by the positioning device. Null means unavailable. */
  speedMetresPerSecond: number | null;
  speedSource: "device" | "calculated" | "unavailable";
  /** Whether the crew had flagged the engine as running for this fix. */
  motorOn: boolean;
  wind: LoggedWind | null;
}

export interface RouteLogMotorEvent {
  id?: number;
  logId: string;
  recordedAt: number;
  motorOn: boolean;
}

export interface RouteLog {
  id: string;
  startedAt: number;
  endedAt: number | null;
  sampleCount: number;
}

export interface ManOverboardEvent {
  id: string;
  recordedAt: number;
  longitude: number;
  latitude: number;
  accuracyMetres: number | null;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("Local log storage failed.")),
      { once: true },
    );
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new Error("Local log storage was interrupted.")),
      { once: true },
    );
    transaction.addEventListener(
      "error",
      () => reject(transaction.error ?? new Error("Local log storage failed.")),
      { once: true },
    );
  });
}

async function openDatabase(): Promise<IDBDatabase> {
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.addEventListener("upgradeneeded", (event) => {
    const database = request.result;
    if (!database.objectStoreNames.contains(LOGS_STORE)) {
      database.createObjectStore(LOGS_STORE, { keyPath: "id" });
    }
    if (!database.objectStoreNames.contains(SAMPLES_STORE)) {
      const samples = database.createObjectStore(SAMPLES_STORE, {
        keyPath: "id",
        autoIncrement: true,
      });
      samples.createIndex("logId", "logId");
      samples.createIndex("logTime", ["logId", "recordedAt"]);
    }
    if (!database.objectStoreNames.contains(MOTOR_EVENTS_STORE)) {
      const motorEvents = database.createObjectStore(MOTOR_EVENTS_STORE, {
        keyPath: "id",
        autoIncrement: true,
      });
      motorEvents.createIndex("logId", "logId");
      motorEvents.createIndex("logTime", ["logId", "recordedAt"]);
    }
    if (!database.objectStoreNames.contains(MAN_OVERBOARD_STORE)) {
      database.createObjectStore(MAN_OVERBOARD_STORE, { keyPath: "id" });
    }
    if (event.oldVersion < 2 && request.transaction) {
      const transaction = request.transaction;
      const motorEvents = transaction.objectStore(MOTOR_EVENTS_STORE);
      const logs = transaction.objectStore(LOGS_STORE).openCursor();
      logs.addEventListener("success", () => {
        const cursor = logs.result;
        if (!cursor) return;
        const log = cursor.value as RouteLog;
        motorEvents.add({
          logId: log.id,
          recordedAt: log.startedAt,
          motorOn: false,
        } satisfies RouteLogMotorEvent);
        cursor.continue();
      });
      const samples = transaction.objectStore(SAMPLES_STORE).openCursor();
      samples.addEventListener("success", () => {
        const cursor = samples.result;
        if (!cursor) return;
        const sample = cursor.value as RouteLogSample;
        cursor.update({ ...sample, motorOn: sample.motorOn === true });
        cursor.continue();
      });
    }
  });
  return requestResult(request);
}

function randomId(prefix: string): string {
  const random = crypto.getRandomValues(new Uint32Array(2));
  return `${prefix}-${Date.now().toString(36)}-${random[0].toString(36)}${random[1].toString(36)}`;
}

export async function startRouteLog(startedAt = Date.now()): Promise<RouteLog> {
  const log: RouteLog = {
    id: randomId("log"),
    startedAt,
    endedAt: null,
    sampleCount: 0,
  };
  const database = await openDatabase();
  try {
    const transaction = database.transaction([LOGS_STORE, MOTOR_EVENTS_STORE], "readwrite");
    transaction.objectStore(LOGS_STORE).add(log);
    transaction.objectStore(MOTOR_EVENTS_STORE).add({
      logId: log.id,
      recordedAt: startedAt,
      motorOn: false,
    } satisfies RouteLogMotorEvent);
    await transactionDone(transaction);
    return log;
  } finally {
    database.close();
  }
}

export async function appendRouteLogMotorEvent(
  logId: string,
  motorOn: boolean,
  recordedAt = Date.now(),
): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction([LOGS_STORE, MOTOR_EVENTS_STORE], "readwrite");
    const log = await requestResult(
      transaction.objectStore(LOGS_STORE).get(logId) as IDBRequest<RouteLog | undefined>,
    );
    if (!log || log.endedAt !== null) {
      transaction.abort();
      throw new Error("This route log is no longer active.");
    }
    transaction.objectStore(MOTOR_EVENTS_STORE).add({
      logId,
      recordedAt,
      motorOn,
    } satisfies RouteLogMotorEvent);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

export async function appendRouteLogSample(
  logId: string,
  sample: Omit<RouteLogSample, "id" | "logId">,
): Promise<number> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction([LOGS_STORE, SAMPLES_STORE], "readwrite");
    const logs = transaction.objectStore(LOGS_STORE);
    const log = await requestResult(logs.get(logId) as IDBRequest<RouteLog | undefined>);
    if (!log || log.endedAt !== null) {
      transaction.abort();
      throw new Error("This route log is no longer active.");
    }
    const nextCount = log.sampleCount + 1;
    logs.put({ ...log, sampleCount: nextCount });
    transaction.objectStore(SAMPLES_STORE).add({ ...sample, logId } satisfies RouteLogSample);
    await transactionDone(transaction);
    return nextCount;
  } finally {
    database.close();
  }
}

export async function finishRouteLog(
  logId: string,
  endedAt = Date.now(),
): Promise<RouteLog | null> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(LOGS_STORE, "readwrite");
    const store = transaction.objectStore(LOGS_STORE);
    const log = await requestResult(store.get(logId) as IDBRequest<RouteLog | undefined>);
    if (!log) {
      await transactionDone(transaction);
      return null;
    }
    const finished = { ...log, endedAt } satisfies RouteLog;
    store.put(finished);
    await transactionDone(transaction);
    return finished;
  } finally {
    database.close();
  }
}

export async function loadRouteLogs(): Promise<RouteLog[]> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(LOGS_STORE, "readonly");
    const logs = await requestResult(
      transaction.objectStore(LOGS_STORE).getAll() as IDBRequest<RouteLog[]>,
    );
    return logs.sort((a, b) => b.startedAt - a.startedAt);
  } finally {
    database.close();
  }
}

export async function loadRouteLogSamples(logId: string): Promise<RouteLogSample[]> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(SAMPLES_STORE, "readonly");
    return await requestResult(
      transaction.objectStore(SAMPLES_STORE).index("logId").getAll(logId) as IDBRequest<
        RouteLogSample[]
      >,
    );
  } finally {
    database.close();
  }
}

export async function loadRouteLogMotorEvents(logId: string): Promise<RouteLogMotorEvent[]> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(MOTOR_EVENTS_STORE, "readonly");
    return await requestResult(
      transaction.objectStore(MOTOR_EVENTS_STORE).index("logId").getAll(logId) as IDBRequest<
        RouteLogMotorEvent[]
      >,
    );
  } finally {
    database.close();
  }
}

function deleteRecordsForLog(store: IDBObjectStore, logId: string): void {
  const request = store.index("logId").openKeyCursor(IDBKeyRange.only(logId));
  request.addEventListener("success", () => {
    const cursor = request.result;
    if (!cursor) return;
    store.delete(cursor.primaryKey);
    cursor.continue();
  });
}

/** Deletes a route log and all of its dependent samples and motor events atomically. */
export async function deleteRouteLog(logId: string): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(
      [LOGS_STORE, SAMPLES_STORE, MOTOR_EVENTS_STORE],
      "readwrite",
    );
    transaction.objectStore(LOGS_STORE).delete(logId);
    deleteRecordsForLog(transaction.objectStore(SAMPLES_STORE), logId);
    deleteRecordsForLog(transaction.objectStore(MOTOR_EVENTS_STORE), logId);
    await transactionDone(transaction);
  } finally {
    database.close();
  }
}

export async function recordManOverboard(
  event: Omit<ManOverboardEvent, "id">,
): Promise<ManOverboardEvent> {
  const stored = { ...event, id: randomId("mob") } satisfies ManOverboardEvent;
  const database = await openDatabase();
  try {
    const transaction = database.transaction(MAN_OVERBOARD_STORE, "readwrite");
    transaction.objectStore(MAN_OVERBOARD_STORE).add(stored);
    await transactionDone(transaction);
    return stored;
  } finally {
    database.close();
  }
}

export async function loadManOverboardEvents(): Promise<ManOverboardEvent[]> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(MAN_OVERBOARD_STORE, "readonly");
    const events = await requestResult(
      transaction.objectStore(MAN_OVERBOARD_STORE).getAll() as IDBRequest<ManOverboardEvent[]>,
    );
    return events.sort((a, b) => b.recordedAt - a.recordedAt);
  } finally {
    database.close();
  }
}
